import React, { useState } from "react";
import PropTypes from "prop-types";
import AppUtils from "@src/utils/appUtils";
import CardContainer from "./CardContainer";
import Icon from "@src/components/atoms/Icon";
import { COMMON_CONTROLS } from "@src/stories/utils/common-controls";

/** A shape of: Flat top, Flat right, inwards curve in between */
const svg_path_top_right_curve =
  'path("M 0 0 L 12 0 L 12 12 A 12 12 0 0 0 0 0 Z")';

/** The top right corner indicator with icon and colours */
const Flipper = ({ iconName, bgColor, iconColor, flipped = false }) => (
  <span
    className={`absolute top-0 right-0 px-3 py-2 rounded-bl-md rounded-tr-md bg-${bgColor} border-l-2 border-l-[rgba(0,0,0,0.2)]`}
  >
    <Icon
      size="lg"
      name={iconName}
      textColor={iconColor}
      backgroundColor="none"
      inline
      padding={false}
      centered={false}
      className={flipped ? "-scale-x-100" : ""}
    />
    {/* "cut-out" (left) */}
    <span
      className={`absolute top-0 right-full translate-x-px block w-3 h-3 bg-${bgColor} pointer-events-none`}
      style={{
        WebkitClipPath: svg_path_top_right_curve,
        clipPath: svg_path_top_right_curve,
      }}
    />
    {/* "cut-out" (bottom) */}
    <span
      className={`absolute top-full -translate-y-px right-0 block w-3 h-3 bg-${bgColor} pointer-events-none`}
      style={{
        WebkitClipPath: svg_path_top_right_curve,
        clipPath: svg_path_top_right_curve,
      }}
    />
  </span>
);

/** Clickable card that can flip over to show a back face */
const FlippableCard = ({
  frontColor = "primary-100",
  backColor = "teal-400",
  iconName = "refresh",
  front,
  back,
  onFlipStart,
  onFlipEnd,
}) => {
  const [flipped, setFlipped] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  const handleClick = () => {
    setIsAnimating(true);
    setFlipped((f) => {
      const newFlipped = !f;
      onFlipStart?.(newFlipped);
      return newFlipped;
    });
  };

  const handleTransitionEnd = () => {
    setIsAnimating(false);
    onFlipEnd?.(flipped);
  };

  return (
    <div className="relative perspective-distant transform-gpu h-full w-full">
      <div
        className={`relative w-full h-full transition-transform duration-500 ${
          flipped ? "rotate-y-180" : ""
        }`}
        onTransitionEnd={handleTransitionEnd}
        style={{
          transformStyle: "preserve-3d",
          transform: flipped ? "rotateY(180deg)" : "rotateY(0deg)",
        }}
      >
        {/* FRONT */}
        <div
          className={`backface-hidden ${flipped && !isAnimating ? "hidden" : ""}`}
        >
          <div className="static h-full">
            <CardContainer
              onClick={handleClick}
              bgColor={frontColor}
              shadow={true}
            >
              {Flipper({ iconName, bgColor: backColor, iconColor: frontColor })}
              <article>{front}</article>
            </CardContainer>
          </div>
        </div>

        {/* BACK */}
        <div
          className={`${!flipped || isAnimating ? "absolute inset-0" : ""} rotate-y-180 backface-hidden ${!flipped && !isAnimating ? "invisible" : ""}`}
        >
          <div className="static">
            <CardContainer
              onClick={handleClick}
              bgColor={backColor}
              shadow={true}
            >
              {Flipper({
                iconName,
                bgColor: frontColor,
                iconColor: backColor,
                flipped: true,
              })}
              <article>{back}</article>
            </CardContainer>
          </div>
        </div>
      </div>
    </div>
  );
};

export const flippableCardControls = {
  frontColor: {
    propType: PropTypes.string,
    description: "Color for the front card",
    defaultValue: "primary-100",
    controlType: "text",
  },
  backColor: {
    propType: PropTypes.string,
    description: "Color for the back card",
    defaultValue: "teal-400",
    controlType: "text",
  },
  iconName: COMMON_CONTROLS.icon_name,
  front: {
    propType: PropTypes.oneOfType([
      PropTypes.node,
      PropTypes.element,
      PropTypes.string,
    ]).isRequired,
    description: "Front card content",
    controlType: "text",
  },
  back: {
    propType: PropTypes.oneOfType([
      PropTypes.node,
      PropTypes.element,
      PropTypes.string,
    ]).isRequired,
    description: "Back card content",
    controlType: "text",
  },
  onFlipStart: {
    propType: PropTypes.func,
    description: "Optional function triggered on click",
    disable: true,
  },
  onFlipEnd: {
    propType: PropTypes.func,
    description: "Optional function triggered after the flip animation ends",
    disable: true,
  },
};

FlippableCard.propTypes = AppUtils.getPropTypeFromControls(
  flippableCardControls,
);

export default FlippableCard;
